import React, { useState, useMemo, useEffect, useCallback, useLayoutEffect, useRef } from 'react';
import { Calendar, dateFnsLocalizer, View } from 'react-big-calendar';
import withDragAndDrop from 'react-big-calendar/lib/addons/dragAndDrop';
import { Filter } from 'lucide-react';
import { format } from 'date-fns/format';
import { isToday } from 'date-fns/isToday';
import { parse } from 'date-fns/parse';
import { startOfWeek } from 'date-fns/startOfWeek';
import { getDay } from 'date-fns/getDay';
import { enUS } from 'date-fns/locale/en-US';
import { parseISO } from 'date-fns';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import { API_BASE_URL } from '../../config/api';
import { useAuth } from '../../contexts/AuthContext';
import { useEscapeKey } from '../../hooks/useEscapeKey';

const locales = {
  'en-US': enUS,
};

const localizer = dateFnsLocalizer({
  format,
  parse,
  startOfWeek,
  getDay,
  locales,
});

// Create drag-and-drop calendar
const DnDCalendar = withDragAndDrop(Calendar);

type SlotMinutes = 15 | 30;
const SLOT_MINUTES_STORAGE_KEY = 'hp_slot_minutes';
const SLOT_MINUTES_EVENT = 'hp_slot_minutes_changed';

export interface TeamMember {
  id: string;
  full_name: string;
  email: string;
  role: string;
}

interface AppointmentCalendarProps {
  appointments: any[];
  onSelectAppointment: (appointmentId: string) => void;
  onSelectSlot?: (slotInfo: { start: Date; end: Date }) => void;
  onRescheduleAppointment?: (appointmentId: string, start: Date, end: Date) => void;
  currentDate?: Date;
  onNavigate?: (date: Date) => void;
  currentView?: View;
  onViewChange?: (view: View) => void;
  selectedVetIds?: Set<string>;
  teamMembers?: TeamMember[];
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  appointments,
  onSelectAppointment,
  onSelectSlot,
  onRescheduleAppointment,
  currentDate,
  onNavigate,
  currentView,
  onViewChange,
  selectedVetIds,
  teamMembers = [],
}) => {
  const { user } = useAuth();
  const [view, setView] = useState<View>(currentView || 'week');
  const [date, setDate] = useState(currentDate || new Date());
  const [blockedTimes, setBlockedTimes] = useState<any[]>([]);
  const [slotMinutes, setSlotMinutes] = useState<SlotMinutes>(() => {
    const saved = localStorage.getItem(SLOT_MINUTES_STORAGE_KEY);
    return saved === '30' ? 30 : 15;
  });
  const calendarWrapperRef = useRef<HTMLDivElement | null>(null);
  const pendingScrollTopRef = useRef<number | null>(null);

  // Use controlled props if provided, otherwise use local state
  const effectiveView = currentView || view;
  const effectiveDate = currentDate || date;

  const captureTimeGridScroll = () => {
    const el = calendarWrapperRef.current?.querySelector('.rbc-time-content') as HTMLElement | null;
    if (el) pendingScrollTopRef.current = el.scrollTop;
  };

  // When switching slot density, react-big-calendar may reset the time grid scrollTop.
  // Restore the previous scrollTop to avoid a jarring "jump" animation.
  useLayoutEffect(() => {
    const el = calendarWrapperRef.current?.querySelector('.rbc-time-content') as HTMLElement | null;
    if (!el) return;
    if (pendingScrollTopRef.current == null) return;
    el.scrollTop = pendingScrollTopRef.current;
    pendingScrollTopRef.current = null;
  }, [slotMinutes]);

  // Keep slot minutes in sync across views (swim lanes + calendars)
  useEffect(() => {
    const handler = (e: Event) => {
      try {
        const detail = (e as CustomEvent).detail as { slotMinutes?: number } | undefined;
        const next = detail?.slotMinutes === 30 ? 30 : 15;
        setSlotMinutes(next);
      } catch {
        // ignore
      }
    };
    window.addEventListener(SLOT_MINUTES_EVENT, handler as EventListener);
    return () => window.removeEventListener(SLOT_MINUTES_EVENT, handler as EventListener);
  }, []);

  // Fetch work schedule availability (blocked times)
  const fetchBlockedTimes = useCallback(async () => {


    if (!user) {
      return;
    }

    if (teamMembers.length === 0) {
      // Don't clear existing blocked times - they're still valid
      // Only skip fetching new data
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        return;
      }

      // Format date for API
      const fetchDate = effectiveDate;
      
      // Calculate date range based on view
      let startDateForFetch: Date;
      let endDateForFetch: Date;
      
      if (effectiveView === 'week') {
        // Calculate the start of the week (Sunday) and end of the week (Saturday)
        const dayOfWeek = fetchDate.getDay(); // 0 for Sunday, 6 for Saturday
        startDateForFetch = new Date(fetchDate);
        startDateForFetch.setDate(fetchDate.getDate() - dayOfWeek); // Go back to Sunday
        endDateForFetch = new Date(startDateForFetch);
        endDateForFetch.setDate(startDateForFetch.getDate() + 6); // Saturday
      } else if (effectiveView === 'month') {
        // First day and last day of the month
        startDateForFetch = new Date(fetchDate.getFullYear(), fetchDate.getMonth(), 1);
        endDateForFetch = new Date(fetchDate.getFullYear(), fetchDate.getMonth() + 1, 0);
      } else {
        // Day view - just the single day
        startDateForFetch = fetchDate;
        endDateForFetch = fetchDate;
      }
      
      // Format dates as YYYY-MM-DD
      const dateStr = `${startDateForFetch.getFullYear()}-${String(startDateForFetch.getMonth() + 1).padStart(2, '0')}-${String(startDateForFetch.getDate()).padStart(2, '0')}`;
      const endDateStr = `${endDateForFetch.getFullYear()}-${String(endDateForFetch.getMonth() + 1).padStart(2, '0')}-${String(endDateForFetch.getDate()).padStart(2, '0')}`;
      
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const vetIdsStr = teamMembers.map(m => m.id).join(',');
      const sampleVetId = teamMembers[0].id;
      
      const url = `${API_BASE_URL}/api/v1/scheduling-unix/vet-availability/${sampleVetId}?date=${dateStr}&end_date=${endDateStr}&vet_ids=${vetIdsStr}&timezone=${timezone}`;
      
      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });


      if (response.ok) {
        const bulkResponse = await response.json();
        
        // Flatten nested response and filter for UNAVAILABLE times
        const allBlockedTimes: any[] = [];
        if (bulkResponse.data) {
          for (const dateKey in bulkResponse.data) {
            for (const vetKey in bulkResponse.data[dateKey]) {
              const events = bulkResponse.data[dateKey][vetKey];
              const unavailable = events.filter((e: any) => 
                e.availability_type === 'UNAVAILABLE' || e.availability_type === 'SURGERY_BLOCK'
              );
              allBlockedTimes.push(...unavailable);
            }
          }
        }

        setBlockedTimes(allBlockedTimes);
      } else {
        console.error('❌ [AppointmentCalendar] Bad response:', await response.text());
      }
    } catch (error) {
      console.error('❌ [AppointmentCalendar] Failed to fetch blocked times:', error);
      setBlockedTimes([]);
    }
  }, [user, teamMembers, effectiveDate, effectiveView]);

  // Fetch blocked times when date, view, or team members change
  useEffect(() => {
    fetchBlockedTimes();
  }, [fetchBlockedTimes]);

  // Get doctor color based on their index in the team members array (matching avatar colors)
  const getDoctorColor = (vetUserId: string): string => {
    const colors = [
      '#3b82f6', // blue-500
      '#10b981', // emerald-500
      '#a855f7', // purple-500
      '#f97316', // orange-500
      '#ec4899', // pink-500
      '#6366f1', // indigo-500
      '#14b8a6', // teal-500
      '#f43f5e', // rose-500
    ];
    
    const index = teamMembers.findIndex(member => member.id === vetUserId);
    if (index === -1) return colors[0]; // Default to blue if not found
    
    return colors[index % colors.length];
  };

  const handleViewChange = (newView: View) => {
    if (onViewChange) {
      onViewChange(newView);
    } else {
      setView(newView);
    }
  };

  const handleNavigate = (newDate: Date) => {
    if (onNavigate) {
      onNavigate(newDate);
    } else {
      setDate(newDate);
    }
  };

  // Convert appointments to calendar events
  const events = useMemo(() => {
    // Filter appointments based on selected vet IDs
    // If no vets are selected, show no appointments
    const filteredAppointments = selectedVetIds && selectedVetIds.size > 0
      ? appointments.filter(apt => apt.assigned_vet_user_id && selectedVetIds.has(apt.assigned_vet_user_id))
      : [];
    

    const appointmentEvents = filteredAppointments.map((appointment) => {
      // Parse UTC date and convert to local time
      const start = parseISO(appointment.appointment_date);
      const end = new Date(start.getTime() + appointment.duration_minutes * 60000);

      // Get pet names
      const petNames = appointment.pets?.map((pet: any) => pet.name).join(', ') || 'No pets';
      
      // Get pet owner name
      const ownerName = appointment.pet_owner?.full_name || 'Unknown';

      return {
        id: appointment.id,
        title: `${petNames} - ${ownerName}`,
        start,
        end,
        resource: appointment,
        // Store vet user ID for color mapping
        vetUserId: appointment.assigned_vet_user_id,
        isBlockedTime: false,
      };
    });

    // Add blocked times as calendar events
    const filteredBlockedTimes = selectedVetIds && selectedVetIds.size > 0
      ? blockedTimes.filter(bt => bt.vet_user_id && selectedVetIds.has(bt.vet_user_id))
      : [];

    const blockedTimeEvents = filteredBlockedTimes.map((blockedTime) => {
      const start = parseISO(blockedTime.start_at);
      const end = parseISO(blockedTime.end_at);

      // Use custom title if provided, otherwise fall back to availability type label
      let title = 'Blocked - Doctor out'; // Default fallback
      if (blockedTime.title) {
        title = blockedTime.title;
      } else if (blockedTime.availability_type) {
        switch (blockedTime.availability_type) {
          case 'UNAVAILABLE':
            title = 'Blocked - Doctor out';
            break;
          case 'SURGERY_BLOCK':
            title = 'Surgery Block';
            break;
          case 'EMERGENCY_ONLY':
            title = 'Emergency Only';
            break;
          default:
            title = 'Blocked - Doctor out';
        }
      }

      return {
        id: `blocked-${blockedTime.id}`,
        title,
        start,
        end,
        resource: blockedTime,
        vetUserId: blockedTime.vet_user_id,
        isBlockedTime: true,
      };
    });

    const allEvents = [...appointmentEvents, ...blockedTimeEvents];

    return allEvents;
  }, [appointments, blockedTimes, selectedVetIds]);

  // Event style getter for color coding - using doctor's avatar color
  const eventStyleGetter = (event: any) => {
    // Style blocked times differently (gray background)
    if (event.isBlockedTime) {
      return {
        style: {
          backgroundColor: '#9CA3AF', // gray-400
          borderRadius: '5px',
          opacity: 0.85,
          color: 'white',
          border: '0px',
          display: 'block',
          fontSize: '13px',
          padding: '2px 5px',
          fontStyle: 'italic',
        },
      };
    }

    // Regular appointments - use vet's color
    const backgroundColor = event.vetUserId 
      ? getDoctorColor(event.vetUserId)
      : '#3B82F6'; // default to blue if no vet assigned

    return {
      style: {
        backgroundColor,
        borderRadius: '5px',
        opacity: 0.9,
        color: 'white',
        border: '0px',
        display: 'block',
        fontSize: '13px',
        padding: '2px 5px',
      },
    };
  };

  const handleSelectEvent = (event: any) => {
    // Don't allow clicking on blocked times
    if (event.isBlockedTime) {
      return;
    }
    onSelectAppointment(event.id);
  };

  const handleSelectSlot = (slotInfo: any) => {
    if (onSelectSlot) {
      onSelectSlot({
        start: slotInfo.start,
        end: slotInfo.end,
      });
    }
  };

  const handleEventDrop = ({ event, start, end }: any) => {
    if (onRescheduleAppointment) {
      onRescheduleAppointment(event.id, start, end);
    }
  };

  const handleEventResize = ({ event, start, end }: any) => {
    if (onRescheduleAppointment) {
      onRescheduleAppointment(event.id, start, end);
    }
  };

  // Custom toolbar to order buttons as: Back | Today | Next
  const CustomToolbar = (toolbar: any) => {
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const filterMenuRef = useRef<HTMLDivElement | null>(null);

    useEscapeKey(() => setIsFilterOpen(false), isFilterOpen);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (!isFilterOpen) return;
        const target = event.target as Node;
        if (filterMenuRef.current && !filterMenuRef.current.contains(target)) {
          setIsFilterOpen(false);
        }
      };

      // Use capture so this still runs even if inner elements call stopPropagation()
      document.addEventListener('mousedown', handleClickOutside, true);
      return () => document.removeEventListener('mousedown', handleClickOutside, true);
    }, [isFilterOpen]);

    const toggleSlotMinutes = () => {
      captureTimeGridScroll();
      const next: SlotMinutes = slotMinutes === 15 ? 30 : 15;
      setSlotMinutes(next);
      localStorage.setItem(SLOT_MINUTES_STORAGE_KEY, String(next));
      try {
        window.dispatchEvent(new CustomEvent(SLOT_MINUTES_EVENT, { detail: { slotMinutes: next } }));
      } catch {
        // ignore
      }
      setIsFilterOpen(false);
    };

    const goToBack = () => {
      toolbar.onNavigate('PREV');
    };

    const goToNext = () => {
      toolbar.onNavigate('NEXT');
    };

    const goToToday = () => {
      toolbar.onNavigate('TODAY');
    };

    const label = () => {
      const date = toolbar.date;
      // Format based on current view
      if (toolbar.view === 'day') {
        return localizer.format(date, 'EEEE, MMMM dd, yyyy', ''); // e.g., "Friday, October 31, 2025"
      } else if (toolbar.view === 'week') {
        return localizer.format(date, 'MMMM yyyy', ''); // e.g., "October 2025"
      } else {
        return localizer.format(date, 'MMMM yyyy', ''); // e.g., "October 2025"
      }
    };

    return (
      <div className="rbc-toolbar">
        <span className="rbc-btn-group flex items-center gap-2">
          <div className="relative" ref={filterMenuRef}>
            <button
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => setIsFilterOpen(!isFilterOpen)}
              title="Options"
              className="inline-flex items-center justify-center text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-dark-bg-hover active:text-gray-900 dark:active:text-white rounded-md p-2 focus:outline-none focus-visible:outline-none"
            >
              <Filter className="w-4 h-4" />
            </button>
            {isFilterOpen && (
              <div className="absolute left-0 top-full mt-2 min-w-[220px] bg-white dark:bg-dark-bg-secondary rounded-lg shadow-2xl border border-gray-200 dark:border-dark-border z-[300] py-1 ring-1 ring-black/5 dark:ring-white/10">
                <button
                  type="button"
                  onClick={toggleSlotMinutes}
                  className="w-full text-left px-4 py-2 text-sm text-gray-900 dark:text-dark-text-primary hover:bg-gray-100 dark:hover:bg-dark-bg-hover transition-colors"
                >
                  {slotMinutes === 15 ? '30 minute slots' : '15 minute slots'}
                </button>
              </div>
            )}
          </div>
          <button type="button" onClick={goToBack}>Back</button>
          <button type="button" onClick={goToToday}>Today</button>
          <button type="button" onClick={goToNext}>Next</button>
        </span>
        <span className="rbc-toolbar-label">{label()}</span>
        <span className="rbc-btn-group">
          <button
            type="button"
            className={toolbar.view === 'month' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('month')}
          >
            Month
          </button>
          <button
            type="button"
            className={toolbar.view === 'week' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('week')}
          >
            Week
          </button>
          <button
            type="button"
            className={toolbar.view === 'day' ? 'rbc-active' : ''}
            onClick={() => toolbar.onView('day')}
          >
            Day
          </button>
        </span>
      </div>
    );
  };

  const CustomDayHeader = ({ date }: { date: Date }) => {
    const today = isToday(date);
    const day = format(date, 'EEE').toUpperCase();
    const dayNum = format(date, 'd');
    return (
      <div className="flex flex-col items-center justify-center leading-none py-1">
        <div className="text-[11px] tracking-wider text-gray-500 dark:text-gray-400">{day}</div>
        <div
          className={[
            'mt-1 w-10 h-10 flex items-center justify-center rounded-full text-[26px] font-medium',
            today ? 'bg-blue-600 text-white' : 'text-gray-900 dark:text-dark-text-primary',
          ].join(' ')}
        >
          {dayNum}
        </div>
      </div>
    );
  };

  const CustomMonthHeader = ({ date }: { date: Date }) => {
    const day = format(date, 'EEE').toUpperCase();
    return (
      <div className="flex items-center justify-center py-1">
        <div className="text-[11px] tracking-wider text-gray-500 dark:text-gray-400">{day}</div>
      </div>
    );
  };

  return (
    <div className="h-full bg-white" ref={calendarWrapperRef}>
      <DnDCalendar
        className={effectiveView === 'month' ? undefined : (slotMinutes === 15 ? 'hp-rbc-slots-15' : 'hp-rbc-slots-30')}
        localizer={localizer}
        events={events}
        startAccessor={(event: any) => event.start}
        endAccessor={(event: any) => event.end}
        style={{ height: 'calc(100vh - 200px)', minHeight: '600px' }}
        view={effectiveView}
        onView={handleViewChange}
        date={effectiveDate}
        onNavigate={handleNavigate}
        onSelectEvent={handleSelectEvent}
        onSelectSlot={handleSelectSlot}
        onEventDrop={handleEventDrop}
        onEventResize={handleEventResize}
        selectable
        resizable
        eventPropGetter={eventStyleGetter}
        popup
        step={slotMinutes}
        timeslots={slotMinutes === 15 ? 4 : 2}
        dayLayoutAlgorithm="no-overlap"
        defaultView="week"
        views={['month', 'week', 'day']}
        components={{
          toolbar: CustomToolbar,
          header: effectiveView === 'month' ? CustomMonthHeader : CustomDayHeader,
        }}
      />
      
      {/* Legend - Doctor colors (only show selected doctors) */}
      {teamMembers.length > 0 && (
        <div className="flex items-center gap-4 p-4 border-t border-gray-200 text-xs flex-wrap">
          <span className="font-medium text-gray-700">Doctors:</span>
          {teamMembers
            .filter(member => selectedVetIds?.has(member.id)) // Only show selected doctors
            .map((member, idx) => {
            const colors = [
              'bg-blue-500',
              'bg-emerald-500',
              'bg-purple-500',
              'bg-orange-500',
              'bg-pink-500',
              'bg-indigo-500',
              'bg-teal-500',
              'bg-rose-500',
            ];
            const color = colors[idx % colors.length];
            
            return (
              <div key={member.id} className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded ${color}`}></div>
                <span>{member.full_name || member.email.split('@')[0]}</span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AppointmentCalendar;

